<script>
  import DisclosureStepper from '../components/DisclosureStepper.svelte';
  import Panel from '../components/Panel.svelte';
  import Section from '../components/Section.svelte';
  import Button from '../components/Button.svelte';
  import Code from '../components/Code.svelte';
  import Alert from '../components/Alert.svelte';
  import { disclosureDraft, clearRawSample, ensureDraft } from '../lib/disclosureDraft.svelte.js';
  import { nav } from '../lib/nav.svelte.js';
  import { onMount } from 'svelte';
  import { validateDisclosure, confirmDisclosure } from '../lib/api.js';
  import { explainErrors } from '../../../lib/disclosure_core.js';
  import { STEPS } from '../../../lib/disclosure_writes.js';

  // idle → checking → saving → done | failed
  let stage = $state('idle');
  let failure = $state(null), result = $state(null);
  const busy = $derived(stage === 'checking' || stage === 'saving');
  onMount(() => ensureDraft(nav.params.source));

  // A rejected config is a list of things to go and fix, so show it as one —
  // named by column, not by array index — and keep the raw shape only as the
  // fallback for a failure that has no field detail at all (a dead backend).
  const problems = $derived(failure ? explainErrors(failure.details ?? {}, disclosureDraft) : []);

  // Saving is three things, and which one broke changes what the operator
  // should do about it. The client runs the check itself; the backend names the
  // write that failed (`step`), so the other two are reportable too.
  const PHASES = [
    { label: 'Check the design against the catalog', steps: [STEPS.check] },
    { label: 'Write mapping.json and aggregations.json', steps: [STEPS.mapping, STEPS.aggregations] },
    { label: 'Recompute the private cubes', steps: [STEPS.recompute] },
  ];

  // Without a named step — a dropped connection, a dead backend — the failure
  // belongs to the whole server call rather than to one write inside it, so
  // both server phases carry it instead of one being blamed on a guess.
  const failedAt = $derived.by(() => {
    if (!failure) return null;
    const named = PHASES.findIndex((p) => p.steps.includes(failure.step));
    return named === -1 ? null : named;
  });

  function statusOf(i) {
    if (result) return 'done';
    if (failure) {
      if (failedAt === null) return i === 0 ? 'done' : 'failed';
      if (i < failedAt) return 'done';
      return i === failedAt ? 'failed' : 'not reached';
    }
    if (stage === 'checking') return i === 0 ? 'running' : 'waiting';
    // The two server phases happen inside one request, so they run together as
    // far as this screen can honestly tell.
    if (stage === 'saving') return i === 0 ? 'done' : 'running';
    return 'waiting';
  }

  const MARK = { done: '✓', failed: '✕', running: '…', waiting: '·', 'not reached': '·' };

  // What is actually being committed, named rather than counted where naming it
  // is the point: a term list and an override count are the two things the JSON
  // used to carry that the summary line did not.
  const kept = $derived(disclosureDraft.mapping?.columns.filter((c) => c.record !== 'shared') ?? []);
  const terms = $derived([...new Set(kept.map((c) => c.target).filter(Boolean))]);
  const overrides = $derived(kept.filter((c) => c.postureOverride || c.treatmentOverride));
  const transforms = $derived(kept.filter((c) => c.transform));
  const shared = $derived(disclosureDraft.aggregations?.aggregates.filter(
    (a) => (a.sharing?.mode ?? 'none') !== 'none') ?? []);

  function save(name, doc) {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([JSON.stringify(doc, null, 2) + '\n'], { type: 'application/json' }));
    a.download = name;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  function downloadBoth() {
    save('mapping.json', disclosureDraft.mapping);
    save('aggregations.json', disclosureDraft.aggregations);
  }

  async function confirm() {
    stage = 'checking'; failure = null; result = null;
    try {
      const draft = { sourceId: disclosureDraft.sourceId, mapping: disclosureDraft.mapping, aggregations: disclosureDraft.aggregations };
      const check = await validateDisclosure(draft);
      if (!check.ok) {
        failure = { message: 'This design cannot be saved yet.', details: check.errors, step: STEPS.check };
        stage = 'failed';
        return;
      }
      stage = 'saving';
      result = await confirmDisclosure(disclosureDraft.sourceId, draft);
      stage = 'done';
      disclosureDraft.dirty = false;
      disclosureDraft.loaded = false;
      clearRawSample();
    } catch (e) {
      failure = {
        message: e.message, details: e.details, rollback: e.rollback,
        step: e.step ?? (stage === 'checking' ? STEPS.check : null),
      };
      stage = 'failed';
    }
  }
</script>

<DisclosureStepper active={2} />
<h1 class="wf2-title wf2-title-level-1">Confirm and save</h1>
{#if disclosureDraft.mapping}<p class="wf2-caption">Active source: <strong>{disclosureDraft.connector?.sourceLabel ?? disclosureDraft.sourceId}</strong> <span class="mono">{disclosureDraft.sourceId}</span></p>{/if}
<p class="wf2-subtitle">Save your design to the Pod and recompute the private cubes.</p>

{#if disclosureDraft.mapping}
  <Section title="What you are saving">
    <Panel>
      <p>
        <span class="mono">{disclosureDraft.sourceId}</span> ·
        {Object.keys(disclosureDraft.mapping.records).length} record types ·
        {kept.length} kept columns ·
        {disclosureDraft.mapping.drop.length} dropped ·
        {disclosureDraft.aggregations.aggregates.length} cubes ·
        hiding counts under {disclosureDraft.aggregations.suppressBelow}
      </p>
      <p class="wf2-caption">
        {transforms.length} columns carry a transform ·
        {overrides.length} carry a posture or treatment override
        {#if shared.length} · {shared.length} cubes keep sharing governance already granted{/if}
      </p>
      <p class="wf2-caption">
        Publishing as: {#each terms as term, i}<span class="mono">{term}</span>{#if i < terms.length - 1}, {/if}{/each}
      </p>

      <div class="wf2-row" style="gap:12px">
        <Button variant="primary" onclick={confirm} disabled={busy}>
          {stage === 'checking' ? 'Checking…' : stage === 'saving' ? 'Saving and computing…' : 'Save both files to the Pod'}
        </Button>
        <Button to={`disclosure/aggregates?source=${encodeURIComponent(disclosureDraft.sourceId)}`}>Back</Button>
      </div>

      <!-- Three phases, not one spinner: which one broke is what decides
           whether the design needs fixing, the Pod needs checking, or neither. -->
      {#if stage !== 'idle'}
        <ul class="wf2-caption" style="list-style:none; padding:0; margin-top: var(--space-2)">
          {#each PHASES as phase, i}
            {@const status = statusOf(i)}
            <li><span class="mono">{MARK[status]}</span> {phase.label} — {status}</li>
          {/each}
        </ul>
      {/if}
    </Panel>
  </Section>

  <!-- Outcome sits with the button that caused it, not below the reference
       material. -->
  {#if failure}
    <Alert title="Could not confirm" variant="danger">
      <p>{failure.message}</p>
      {#if failure.rollback}
        <!-- The one failure mode that needs a human to go and look. -->
        <p><strong>Your Pod was not fully put back:</strong> {failure.rollback.join('; ')}</p>
      {:else if failedAt === 2}
        <p>
          Both config files were restored. The private cubes may be left partly
          recomputed — they are derived, so the next ingest or cube save
          rebuilds them in full, and nothing was published either way.
        </p>
      {/if}
      {#if problems.length}
        <ul>
          {#each problems as problem}
            <li>
              {#if problem.where}<strong>{problem.where}</strong> — {/if}{problem.what}
              <span class="wf2-caption">in {problem.file}</span>
            </li>
          {/each}
        </ul>
        <Button to="disclosure/label">Back to assess and label</Button>
      {:else if failure.details}
        <Code lang="json" text={JSON.stringify(failure.details, null, 2)} />
      {/if}
    </Alert>
  {/if}

  {#if result}
    <Alert title="Saved to your Pod" variant="success">
      <p>
        The mapping and cubes are committed. The technical owner can run Connector ingest;
        governance publishes separately.
      </p>
      <div class="wf2-row" style="gap:12px">
        <Button to="records">View Records</Button>
        <Button to="queries">Open Queries</Button>
      </div>
    </Alert>

    <!-- The recompute already counted every cube; until now the screen threw
         that away and left the analyst to go and look. This is the same
         suppression arithmetic the aggregate builder previewed, after the fact
         and over the whole record set rather than a sample. -->
    <Section title="What each private cube now holds">
      <Panel>
        <p class="wf2-caption">
          {result.recompute.recordCount} records · hiding counts under
          {disclosureDraft.aggregations.suppressBelow}. These cubes are private;
          governance publishes separately.
        </p>
        <div class="wf2-tablewrap" style="overflow-x:auto">
          <table class="wf2-table">
            <thead><tr><th>Cube</th><th>Published groups</th><th>Hidden</th></tr></thead>
            <tbody>
              {#each result.recompute.slices as slice}
                <tr>
                  <td>
                    <span class="mono">{slice.dataSet}</span>
                    {#if slice.theme}<span class="tag {slice.theme}" style="margin-left:8px">{slice.theme}</span>{/if}
                  </td>
                  <td>{slice.kept} of {slice.cells}</td>
                  <td>{slice.suppressed}</td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      </Panel>
    </Section>
  {/if}

  <!-- The two files are the record of what was decided, not the review surface:
       the decisions were made on the screens that made them, and 300 lines of
       JSON between the summary and the button only pushed the button out of
       sight. Available to whoever wants to read them, in the reader they belong
       in. -->
  <Section title="The two files">
    <Panel>
      <p class="wf2-caption">
        Your design is these two declarative files. The connector reads the same mapping you authored
        here — nothing is translated in between.
      </p>
      <div class="wf2-row" style="gap:12px">
        <Button variant="secondary" onclick={downloadBoth}>Download both</Button>
        <Button onclick={() => save('mapping.json', disclosureDraft.mapping)}>Download mapping.json</Button>
        <Button onclick={() => save('aggregations.json', disclosureDraft.aggregations)}>
          Download aggregations.json
        </Button>
      </div>
    </Panel>
  </Section>
{:else}
  <Alert title="Choose a source first"><Button to="disclosure">Open sources</Button></Alert>
{/if}
